<script>
import Icon from "../../components/Icon.vue";
import LoadingSpinner from "../../components/loadingSpinner.vue";
import ThankYouAnimation from "../../animations/ThankYou.vue";
import FinishedAnimation from "../../animations/Finished.vue";
import Popup from "../../components/popup.vue";
import {useReportStore} from "../../../stores/report";
import {useAnalyticEventStore} from "../../../stores/analyticEvents";
import {useOrganizationStore} from "../../../stores/organizations";

export default {
  name: 'DonorReportExporter',
  components: {Popup, FinishedAnimation, ThankYouAnimation, LoadingSpinner, Icon},

  props: {
    reportType: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: false
    },
    description: {
      type: String,
      required: false
    },
    showYears: {
      type: Boolean,
      default: false
    }
  },
  emits: ['generate-report'],

  setup() {
    const reportStore = useReportStore();
    const analyticEventStore = useAnalyticEventStore();
    const organizationStore = useOrganizationStore();

    return {
      reportStore,
      analyticEventStore,
      organizationStore
    };
  },

  data() {
    return {
      donors: [],
      selectedDonors: [],
      selectedYear: null,
      searchQuery: '',
      loading: true,
      generating: false,
      finished: false,
      visibleYearCount: 5,
      uploadedReportsCount: 0,
      popupMessage: "Something went wrong generating the report!",
      paginatedDonorList: 50
    };
  },

  computed: {

    generateButtonEnabled() {
      if (this.selectedDonors.length === 0) return false;
      return !(this.showYears && !this.selectedYear);

    },

    filteredAvailableDonors() {
      if (!this.searchQuery.trim()) {
        return this.donors;
      }

      const query = this.searchQuery.toLowerCase().trim();
      return this.donors.filter(donor => {
        const name = (donor.user_details?.f_name || '' + donor.user_details?.l_name).toLowerCase();
        const email = (donor.user_details.email || '').toLowerCase();
        return name.includes(query) || email.includes(query);
      });
    },

    years() {
      const currentYear = new Date().getFullYear();
      const years = [];

      // Generate years from current year going backwards
      for (let i = 0; i < this.visibleYearCount; i++) {
        years.push(currentYear - i);
      }

      return years;
    },
    visibleDonors() {
      return this.filteredAvailableDonors.slice(0, this.paginatedDonorList);
    },
    hasMoreDonors() {
      return this.filteredAvailableDonors.length > this.paginatedDonorList;
    },

    // Number of reports uploaded for progress indicator
  },

  watch: {
    '$route.params.org'() {
      this.donors = [];
      this.selectedDonors = [];
      this.loadDonors();
    },
    preselectedDonors: {
      immediate: true,
      handler(newDonors) {
        if (newDonors && newDonors.length > 0) {
          this.selectedDonors = [...newDonors];
        }
      }
    },
    selectedDonors: {
      immediate: true,
      deep: true,
      handler() {
        this.countUploadedReports();
      }
    },
    searchQuery() {
      this.paginatedDonorList = 50;
    },
    selectedYear() {
      if (this.showYears) {
        this.loadDonors();
      }
    },
  },

  mounted() {
    if (this.showYears && !this.selectedYear) {
      this.selectedYear = new Date().getFullYear();
    }
    this.loadDonors();
  },

  methods: {
    loadDonors() {
      this.loading = true;
      // For year-scoped reports (Year-End Summary, Contribution Statement) limit
      // the list to donors who gave in the selected year (WI-006). The Donor List
      // report has no year and loads all donors.
      const givingYear = this.showYears ? this.selectedYear : null;
      this.reportStore.getUnscopedDonors(givingYear)
        .then((donors) => {
          this.donors = donors;
          // Drop any selected donors that are no longer in the fitlered list
          const availableIds = new Set(this.donors.map(d => d.id));
          this.selectedDonors = this.selectedDonors.filter(d => availableIds.has(d.id));
          this.loading = false;
        });
    },

    addDonor(donor) {
      if (!this.selectedDonors.find(selected => selected.id === donor.id)) {
        this.selectedDonors.push(donor);
      }
    },

    toggleDonor(donor) {
      if (this.generating || this.finished) return;
      if (this.donorIsSelected(donor)) {
        this.removeDonor(donor);
      } else {
        this.addDonor(donor);
      }
    },

    removeDonor(donor) {
      this.selectedDonors = this.selectedDonors.filter(selected => selected.id !== donor.id);
    },

    addAllDonors() {
      if (this.filteredAvailableDonors.length > 100) {
        this.popupMessage = `Adding ${this.filteredAvailableDonors.length} donors. Generating reports for this many donors may take a few minutes.`;
        this.$refs.popup.show();
      }
      this.filteredAvailableDonors.forEach(donor => {
        this.addDonor(donor);
      });
    },

    clearSelectedDonors() {
      this.selectedDonors = [];
    },

    clearSearch() {
      this.searchQuery = '';
    },

    reset() {
      this.selectedDonors = [];
      this.finished = false;
      this.generating = false;

      this.donors.forEach(donor => {
        donor.pendingReportId = null;
        donor.pendingReportStatus = null;
      });
    },

    generateReport() {
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: `report-generated-${this.reportType}` });
      switch (this.reportType) {
        case 'donor_list':
          this.exportDonorList();
          break;
        case 'year_end_summary':
          this.exportYearEndSummaryReports();
          break;
        case 'contribution_statement':
          this.exportContributionStatements();
          break;
      }
    },

    exportYearEndSummaryReports() {
      this.generating = true;
      this.processInBatches(this.selectedDonors, (donor) => {
        return this.reportStore.createDonorYearEndSummaryReport(donor, this.selectedYear);
      }).then((reportIds) => {
        // WI-007: donors with no giving that year are skipped (null id) - exclude them from the zip.
        const validReportIds = reportIds.filter(id => id);
        if (validReportIds.length === 0) {
          this.popupMessage = "No donors gave in the selected year. Please select a different year.";
          this.$refs.popup.show();
          this.reset();
          return null;
        }
        return this.reportStore.downloadMultipleReportsZipped(validReportIds, this.selectedDonors);
      }).then(_ => this.finished = true)
        .catch((e) => {
          this.popupMessage = e?.message || "Something went wrong generating the report!";
          this.$refs.popup.show();
          this.reset();
        });
    },

    // TODO: Extract to shared utility (also duplicated in HouseholdReportExporter.vue)
    // Processes items in parallel batches to avoid overwhelming the browser with too many concurrent requests.
    // For bulk PDFs
    async processInBatches(items, fn, batchSize = 25) {
      const results = [];
      for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);
        const batchResults = await Promise.all(batch.map(fn));
        results.push(...batchResults);
      }
      return results;
    },

    exportContributionStatements() {
      this.generating = true;
      this.processInBatches(this.selectedDonors, (donor) => {
        return this.reportStore.createDonorDonationReport(donor, this.selectedYear);
      }).then((reportIds) => {
        // WI-007: donors with no giving that year are skipped (null id) - exclude them from the zip.
        const validReportIds = reportIds.filter(id => id);
        if (validReportIds.length === 0) {
          this.popupMessage = "No donors gave in the selected year. Please select a different year.";
          this.$refs.popup.show();
          this.reset();
          return null;
        }
        return this.reportStore.downloadMultipleReportsZipped(validReportIds, this.selectedDonors);
      }).then(_ => this.finished = true)
        .catch((e) => {
          this.popupMessage = e?.message || "Something went wrong generating the report!";
          this.$refs.popup.show();
          this.reset();
        });
    },

    exportDonorList() {
      this.generating = true;
      this.reportStore.createDonorList(this.selectedDonors)
        .then((res) => {
          // Get the created record and poll the report until complete
          const id = res.id;
          if (!id) {
            this.popupMessage = res?.error || "Something went wrong generating the report!";
            this.$refs.popup.show();
            this.generating = false;
          }
          this.reportStore.pollReport(id)
            .then((record) => {
              // Report has been created, download the file

              this.reportStore.downloadCSVReport(record);
              this.generating = false;
            });
        }).catch((e) => {
        this.popupMessage = e?.message || "Something went wrong generating the report!";
        this.$refs.popup.show();
        this.reset();
      });
    },

    donorIsSelected(donor) {
      return this.selectedDonors.find(selected => selected.id === donor.id);
    },

    selectYear(year) {
      if (this.generating || this.finished) return;
      this.selectedYear = year;
    },
    loadMoreYears() {
      this.visibleYearCount += 5;
    },
    countUploadedReports() {
      let count = 0;

      // Progress indicator wasn't moving, counting different statuses as values to move it along
      this.selectedDonors.forEach(donor => {
        if (donor.pendingReportStatus === "COMPLETED") {
          count += 1;
        }
        if (donor.pendingReportStatus === "SKIPPED") {
          count += 1;
        }
        if (donor.pendingReportStatus === "PROCESSING") {
          count += 0.5;
        }
        if (donor.pendingReportStatus === "CREATED") {
          count += 0.25;
        }
      });

      const i = this.selectedDonors.filter(d => d.pendingReportStatus === "COMPLETED").length;
      this.uploadedReportsCount = count;
    }

  }
};
</script>

<template>
  <div
    v-if="loading"
    class="min-h-60 flex justify-center content-center"
  >
    <loading-spinner size="small" />
  </div>
  <div
    v-else
    class="report-generator-container"
  >
    <div
      v-if="title || description"
      class="header"
    >
      <div class="headline-3-regular">
        {{ title }}
      </div>
      <div class="body-2-light">
        {{ description }}
      </div>
    </div>
    <div
      v-if="showYears"
      class="year-selector"
    >
      <div class="section-label">
        Select Year
      </div>
      <div class="flex flex-row gap-3 flex-wrap items-center">
        <div
          v-for="year in years"
          :key="year"
          :class="{selected: year === selectedYear}"
          class="year-chip"
          @click="selectYear(year)"
        >
          {{ year.toString() }}
        </div>
        <button
          class="show-more-btn text-xs px-3 py-1 rounded hover:bg-gray-100 transition-colors"
          @click="loadMoreYears"
        >
          Show More
        </button>
      </div>
    </div>


    <div class="content-container">
      <div class="flex flex-col flex-1">
        <!-- Search/ add all, clear -->
        <div class="controls-container">
          <div class="w-full flex flex-row justify-between mb-1">
            <div class="section-label">
              Select Donors to Include
            </div>
            <div class="font-semibold text-gray-500">
              ({{ selectedDonors.length }} of {{ donors.length }} selected)
            </div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="search-input-wrapper flex-1">
              <icon
                icon="search"
                class="search-icon"
              />

              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search by name or email..."
                class="search-input"
              >
              <button
                v-if="searchQuery"
                class="clear-search-btn"
                @click="clearSearch"
              >
                <icon icon="x" />
              </button>
            </div>

            <button
              :disabled="filteredAvailableDonors.length === 0 || generating || finished"
              class="btn btn-primary btn-small"
              @click="addAllDonors"
            >
              Add All
            </button>

            <button
              :disabled="selectedDonors.length === 0 || generating || finished"
              class="btn btn-outline btn-secondary btn-small"
              @click="clearSelectedDonors"
            >
              <span>Clear All </span>
            </button>
          </div>
        </div>
        <!-- Donor list -->

        <div class="donors-list clean-scrollbar">
          <div
            v-if="filteredAvailableDonors.length === 0 && searchQuery"
            class="empty-state"
          >
            <p class="empty-primary">
              No donors found
            </p>
            <p class="empty-secondary">
              Try adjusting your search criteria
            </p>
          </div>
          <div
            v-else-if="filteredAvailableDonors.length === 0"
            class="empty-state"
          >
            <p class="empty-primary">
              All donors selected
            </p>
            <p class="empty-secondary">
              No more donors available to add
            </p>
          </div>

          <div
            class="donor-list-container"
            :class="{generating: generating}"
          >
            <div
              v-for="donor in visibleDonors"
              :key="`${donor.id}`"
              class="donor-info-card"
              :class="[
                donorIsSelected(donor) ? 'selected-donor' : 'unselected-donor',
                !generating || donorIsSelected(donor) ? 'visible' :'collapsed']"
              @click="toggleDonor(donor)"
            >
              <div class="user-info">
                <div class="user-details">
                  <div class="user-name">
                    <span>{{ donor.user_details.f_name }}</span>
                    <span> {{ donor.user_details.l_name }}</span>
                  </div>
                  <div class="user-email">
                    {{ donor.user_details.email }}
                  </div>
                </div>
              </div>
              <div
                class="user-actions"
              >
                <button
                  v-if="!donorIsSelected(donor) && !generating"
                  class="add-btn"
                  title="Add user"
                >
                  <icon
                    icon="plus"
                    size="16px"
                  />
                </button>
                <button
                  v-else-if="donorIsSelected(donor) && !generating"
                  class="remove-btn"
                  title="Remove user"
                >
                  <icon
                    icon="check"
                    color="darkgreen"
                    class="default-icon"
                  />
                  <icon
                    icon="x"
                    color="red"
                    class="hover-icon"
                  />
                </button>
              </div>
            </div>
            <button
              v-if="hasMoreDonors"
              class="load-more-donors-btn"
              @click="paginatedDonorList += 50"
            >
              Show More ({{ filteredAvailableDonors.length - paginatedDonorList }} remaining)
            </button>
          </div>
        </div>
      </div>
      <div class="flex-1 flex flex-col items-center content-center mt-32">
        <div
          v-if="finished"
          class="flex flex-col w-full items-center gap-4 mb-12"
        >
          <div class="text-lg">
            Your Reports will download shortly ...
          </div>
          <div class="flex flex-row gap-2">
            <button
              class="btn btn-filled"
              @click="reset"
            >
              Another Report
            </button>
          </div>
        </div>

        <div v-else-if="generating">
          <loading-spinner
            size="small"
            :max="selectedDonors.length"
            :value="uploadedReportsCount"
            percentage
            prevent-full-percent
          />
        </div>

        <button
          v-else
          role="button"
          :disabled="!generateButtonEnabled"
          class="btn btn-filled flex flex-row"
          @click="generateReport"
        >
          <span>Download</span>
          <icon
            icon="cloud-arrow-down"
            size="24px"
          />
        </button>
      </div>
    </div>
  </div>
  <popup
    ref="popup"
    :message="popupMessage"
    :duration="5000"
  />
</template>


<style lang="scss" scoped>

.report-generator-container {
  @apply w-full flex flex-col h-full max-h-full;
  //@apply p-2;

  @screen md {
    @apply p-4;
  }

  .year-chip {
    @apply py-0.5 px-2 rounded-md font-semibold cursor-pointer;
    @apply flex items-center content-center;
    @apply border-2;
    &:hover, &.selected {
      @apply bg-blue-500 text-white;
    }

    &.selected {
      @apply border-none;
    }
  }

  .content-container {
    @apply flex flex-row overflow-y-auto h-full;
    @apply border p-3 rounded-md bg-gray-50;
  }
}

.header {
  @apply pb-4 border-b-2 mb-4 border-gray-200;
}

.section-label {
  @apply font-semibold;
}

.divider {
  @apply w-px bg-gray-200 my-5;
}

.year-selector {
  @apply mb-4;
}

.controls-container {
  @apply mb-2;
  .search-input-wrapper {
    @apply relative flex items-center w-full;
  }

  .search-icon {
    @apply absolute left-3 text-gray-400 pointer-events-none;
  }

  .search-input {
    @apply w-full py-2 px-3 pl-9 pr-10 border border-gray-300 rounded-md text-sm outline-none;
    @apply transition-all duration-150 focus:border-blue-500 focus:ring-2 focus:ring-blue-100;
  }
}

.donors-list {
  @apply h-full flex flex-col gap-1 overflow-y-auto border rounded-md bg-white;
  @apply p-3 pb-16;
}

.empty-state {
  @apply text-center py-12;
}

.empty-primary {
  @apply text-gray-900 font-medium mb-1;
}

.empty-secondary {
  @apply text-gray-500 text-sm;
}

.donor-list-container {
  @apply flex flex-col gap-2;
  .donor-info-card {
    @apply flex items-center justify-between p-2 bg-gray-50;
    @apply border border-gray-200 rounded-lg cursor-pointer transition-all;

    &.collapsed {
      animation: collapseHeight .6s ease-in-out forwards;
    }

    @apply h-16;
    &:hover {
      @apply shadow-sm border-gray-300;
    }
  }

  .selected-donor {
    @apply bg-green-50 border-green-800;

    &:hover {
      @apply bg-green-100 border-green-800;
    }
  }

  .unselected-donor {
    &:hover {
      @apply bg-gray-50;
    }

  }

  .user-info {
    @apply flex items-center gap-3 flex-1 min-w-0;
  }

  .user-details {
    @apply min-w-0 flex-1;
  }

  .user-name {
    @apply font-medium text-gray-900 flex gap-1;
  }

  .user-email {
    @apply text-sm text-gray-500 truncate;
  }

}

.btn.btn-filled {
  :deep(svg) path {
    fill: white;
  }
}

.remove-btn .hover-icon {
  display: none;
}

.remove-btn .default-icon {
  display: inline;
}

.remove-btn:hover .hover-icon {
  display: inline;
}

.remove-btn:hover .default-icon {
  display: none;
}

.load-more-donors-btn {
  @apply w-full py-2 mt-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors;
}

@keyframes collapseHeight {
  0% {
    opacity: 1;
  }
  40% {
    @apply h-8 m-0 opacity-60;
  }
  60% {
    @apply h-4 opacity-20;
  }
  80% {
    @apply h-0 opacity-0;
  }
  100% {
    @apply h-0 opacity-0;
    display: none;
  }
}

</style>
